import { getBusinessSlugAdvisory } from "../../platform/util/businessSlug";
import React, { useContext, useRef, useState } from "react";
import { gql, useApolloClient, useMutation, useQuery } from "@apollo/client";
import { Alert, Box, Button, Card, CardContent, Chip, CircularProgress, FormControlLabel, Link, MenuItem, Stack, Switch, TextField, Typography } from "@mui/material";
import { UserContext } from "../../platform/components/auth/auth-context";
import { UPDATE_DEFAULT_BUSINESS } from "../../platform/graphql/mutations";
import { useTimeZones } from "../../platform/util/timezones";
import { GET_TOKEN } from "../../platform/graphql/queries";
import { setToken } from "../../platform/util/authUtils";
import { preferredStartPath } from "../../platform/util/startPage";
import { useSelectedBusiness } from "../../platform/redux/store";

export const WORKSPACES = gql`query Workspaces {
  myBusinesses { id name url createdBy workspaceType canManagePersonalPolicy }
  personalWorkspaceAccess { enabled canCreate existingPersonalId approvals { businessId businessName staffId status } }
}`;
export const CREATE = gql`mutation CreateWorkspace($type: WorkspaceType!, $name: String!, $url: String!, $timeZoneId: Int!) {
  createWorkspace(type: $type, name: $name, url: $url, timeZoneId: $timeZoneId) { id name url workspaceType }
}`;
const FEATURE = gql`mutation WorkspaceFeature($enabled: Boolean!) { setPersonalWorkspaceFeature(enabled: $enabled) }`;
const REQUEST = gql`mutation RequestPersonalWorkspace { requestPersonalWorkspace { enabled canCreate } }`;
const POLICY = gql`query WorkspacePolicy($businessId: ID!) { workspacePolicy(businessId: $businessId) { businessId allowPersonalWorkspaces members { staffId name status } } }`;
const SET_POLICY = gql`mutation WorkspacePolicy($businessId: ID!, $allowed: Boolean!) { setWorkspacePersonalPolicy(businessId: $businessId, allowed: $allowed) }`;
const REVIEW = gql`mutation ReviewWorkspacePermission($staffId: ID!, $approved: Boolean!) { reviewPersonalWorkspaceRequest(staffId: $staffId, approved: $approved) }`;
const PROFILE = gql`mutation PersonalProfile($input: BusinessInput) { updateBusiness(input: $input) { id name url } }`;

function PersonalProfile({ business, onChange }: any) {
  const [name, setName] = useState(business.name);
  const [url, setUrl] = useState(business.url);
  const [save, state] = useMutation(PROFILE);
  const [message, setMessage] = useState("");
  const hasChanges = name !== business.name || url !== business.url;
  return <Stack component="form" spacing={2} sx={{ mt: 2 }} onSubmit={async (event: React.FormEvent) => {
    event.preventDefault();
    if (!hasChanges || state.loading || getBusinessSlugAdvisory(url)) return;
    try { setMessage(""); await save({ variables: { input: { id: business.id, name, url } } }); await onChange(); setMessage("Personal profile saved."); } catch (e: any) { setMessage(e.message); }
  }}>
    <Typography variant="h6">Personal profile</Typography>
    <TextField required label="Your public name" value={name} onChange={e => setName(e.target.value)} />
    <TextField required label="Booking link" value={url} onChange={e => setUrl(e.target.value)} inputProps={{ pattern: "[a-z0-9]+(-[a-z0-9]+)*" }} error={Boolean(getBusinessSlugAdvisory(url))} helperText={getBusinessSlugAdvisory(url) || "Changing your link makes the old address unavailable."} />
    <Button type="submit" disabled={!hasChanges || state.loading || Boolean(getBusinessSlugAdvisory(url))}>Save personal profile</Button>
    {message && <Alert severity={message === "Personal profile saved." ? "success" : "error"}>{message}</Alert>}
  </Stack>;
}

function Policy({ business, onChange }: any) {
  const { data, loading, error, refetch } = useQuery(POLICY, { variables: { businessId: business.id }, fetchPolicy: "network-only" });
  const [save, saving] = useMutation(SET_POLICY);
  const [review, reviewing] = useMutation(REVIEW);
  const [message, setMessage] = useState("");
  const run = async (action: () => Promise<any>) => {
    try { setMessage(""); await action(); await refetch(); await onChange(); } catch (e: any) { setMessage(e.message); }
  };
  if (loading) return <CircularProgress size={20} />;
  if (error) return <Alert severity="error">{error.message}</Alert>;
  const policy = data.workspacePolicy;
  return <Stack spacing={1} sx={{ mt: 2 }}>
    <Typography variant="h6">Personal workspace permissions</Typography>
    <Typography variant="body2">Allow creation for your team, or approve individual members. Existing personal bookings are preserved when permission is turned off.</Typography>
    <FormControlLabel label="Allow team members to create personal workspaces" control={<Switch checked={policy.allowPersonalWorkspaces} disabled={saving.loading} onChange={(_, allowed) => run(() => save({ variables: { businessId: business.id, allowed } }))} />} />
    {policy.members.map((member: any) => <Stack key={member.staffId} direction="row" spacing={1} alignItems="center" flexWrap="wrap">
      <Typography sx={{ flex: 1 }}>{member.name}</Typography><Chip size="small" label={member.status === "NONE" ? "Not requested" : member.status.toLowerCase()} />
      <Button disabled={reviewing.loading} onClick={() => run(() => review({ variables: { staffId: member.staffId, approved: true } }))}>Approve</Button>
      <Button disabled={reviewing.loading} onClick={() => run(() => review({ variables: { staffId: member.staffId, approved: false } }))}>Deny</Button>
    </Stack>)}
    {message && <Alert severity="error">{message}</Alert>}
  </Stack>;
}

export default function WorkspacesPage() {
  const client = useApolloClient();
  const { timeZones } = useTimeZones();
  const { providerUser } = useContext(UserContext);
  const selectedBusiness = useSelectedBusiness();
  const { data, loading, error, refetch } = useQuery(WORKSPACES, { fetchPolicy: "network-only" });
  const [create, creating] = useMutation(CREATE);
  const [feature, featureState] = useMutation(FEATURE);
  const [request, requesting] = useMutation(REQUEST);
  const [switchWorkspace, switching] = useMutation(UPDATE_DEFAULT_BUSINESS);
  const [type, setType] = useState("BUSINESS");
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [zone, setZone] = useState(Intl.DateTimeFormat().resolvedOptions().timeZone);
  const [message, setMessage] = useState("");
  const [policyId, setPolicyId] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const inFlight = useRef(false);
  const navigating = useRef(false);
  const createdId = useRef<string | null>(null);
  const access = data?.personalWorkspaceAccess;
  const businesses = data?.myBusinesses || [];
  const currentWorkspaceId = Number(selectedBusiness?.businessBasic?.id || providerUser?.defaultBusinessId);
  const currentWorkspace = businesses.find((business: any) => Number(business.id) === currentWorkspaceId);
  const personalBlocked = type === "PERSONAL" && !access?.canCreate;
  const changeWorkspace = async (id: string) => {
    try {
      setMessage("");
      await switchWorkspace({ variables: { userId: Number(providerUser.userId), defaultBusinessId: Number(id) } });
      const session = await client.query({ query: GET_TOKEN, variables: { businessId: Number(id), userId: Number(providerUser.userId), staffId: null, subscriptionId: null }, fetchPolicy: "network-only" });
      setToken(session.data.getToken.token);
      localStorage.setItem("selectedBusinessId", String(id));
      // Reload refreshes the account token and clears the previous workspace's in-memory selection.
      localStorage.removeItem("redirectAfterBusiness");
      localStorage.removeItem("redirectAfterLogin");
      const homePath = preferredStartPath(providerUser);
      // Opening a workspace must leave the workspace picker, even if the
      // picker itself is the account's preferred page after login.
      window.location.assign(homePath && homePath !== "/workspaces" ? homePath : "/biz/bookingtypes?v=booking");
      navigating.current = true;
    } catch (e: any) { setMessage(e.message); }
  };
  const run = async (action: () => Promise<any>) => {
    try { setMessage(""); await action(); await refetch(); } catch (e: any) { setMessage(e.message); }
  };
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (inFlight.current || personalBlocked || getBusinessSlugAdvisory(slug)) return;
    inFlight.current = true;
    setBusy(true);
    try {
      setMessage("");
      const timeZoneId = Number(timeZones.find((item: any) => item.timezone === zone)?.id);
      if (!timeZoneId) throw new Error("Enter a supported time zone, such as America/Los_Angeles.");
      if (!createdId.current) {
        const result = await create({ variables: { type, name, url: slug, timeZoneId } });
        createdId.current = result.data.createWorkspace.id;
      }
      await refetch();
      setShowCreate(false);
      await changeWorkspace(createdId.current!);
    } catch (e: any) { setMessage(e.message); }
    finally { if (!navigating.current) { inFlight.current = false; setBusy(false); } }
  };
  const openWorkspace = async (id: string) => {
    if (inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try { await changeWorkspace(id); }
    finally { if (!navigating.current) { inFlight.current = false; setBusy(false); } }
  };
  if (loading) return <Box sx={{ p: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error.message}<Button onClick={() => refetch()}>Retry</Button></Alert>;
  return <Box sx={{ maxWidth: 900, mx: "auto", p: { xs: 2, md: 4 } }}>
    <Stack spacing={3}>
      <Box><Typography variant="h4">Your workspaces</Typography><Typography color="text.secondary">One account. Separate calendars, booking pages, and settings.</Typography></Box>
      {currentWorkspace && <Alert severity="info">Current workspace: <strong>{currentWorkspace.name}</strong> ({currentWorkspace.workspaceType === "PERSONAL" ? "Personal" : "Business"}). Choose Go to home to open your start page, or switch to another workspace below.</Alert>}
      {message && <Alert severity="error">{message}</Alert>}
      {providerUser?.isPlatformAdmin && <Card variant="outlined"><CardContent>
        <Typography variant="h6">Platform settings</Typography>
        <FormControlLabel label="Enable personal workspaces" control={<Switch checked={Boolean(access.enabled)} disabled={featureState.loading} onChange={(_, enabled) => run(() => feature({ variables: { enabled } }))} />} />
        <Typography variant="body2">Enables new personal workspaces. Business team members still need their business administrator's permission.</Typography>
      </CardContent></Card>}
      {businesses.map((business: any) => <Card key={business.id} variant="outlined" sx={Number(business.id) === currentWorkspaceId ? { borderColor: "primary.main", borderWidth: 2 } : undefined}><CardContent>
        <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap">
          <Typography variant="h6" sx={{ flex: 1 }}>{business.name}</Typography>
          <Chip size="small" label={business.workspaceType === "PERSONAL" ? "Personal" : "Business"} />
          {Number(business.id) === currentWorkspaceId && <Chip size="small" color="primary" label="Current workspace" />}
          <Button variant={Number(business.id) === currentWorkspaceId ? "contained" : "outlined"} disabled={busy} onClick={() => openWorkspace(business.id)}>{Number(business.id) === currentWorkspaceId ? "Go to home" : "Switch to workspace"}</Button>
        </Stack>
        <Link href={`/${encodeURIComponent(business.url)}`}>Preview public booking page</Link>
        {business.workspaceType === "PERSONAL" && <PersonalProfile business={business} onChange={refetch} />}
        {business.canManagePersonalPolicy && <Box><Button onClick={() => setPolicyId(policyId === business.id ? null : business.id)}>Team permissions</Button>
          {policyId === business.id && <Policy business={business} onChange={refetch} />}</Box>}
      </CardContent></Card>)}
      {businesses.length > 0 && !showCreate && <Button disabled={busy} onClick={() => {
        createdId.current = null;
        setName(""); setSlug(""); setMessage(""); setShowCreate(true);
      }}>Add workspace</Button>}
      {(showCreate || !businesses.length) && <Card variant="outlined"><CardContent><Stack component="form" onSubmit={submit} spacing={2}>
        <Typography variant="h5">{businesses.length ? "Add workspace" : "How will you use Timely?"}</Typography>
        <TextField select label="Workspace type" value={type} onChange={e => { setType(e.target.value); if (e.target.value === "PERSONAL" && !name) setName(providerUser?.username || ""); }}>
          <MenuItem value="PERSONAL">For myself</MenuItem><MenuItem value="BUSINESS">For a business or team</MenuItem>
        </TextField>
        <Typography>{type === "PERSONAL" ? "Let people book time with you. Your workspace is private from your business team." : "Manage bookings for your business and invite your team. Business details can be added later."}</Typography>
        {type === "PERSONAL" && !access.enabled && <Alert severity="info">Personal workspaces must first be enabled by the platform administrator.</Alert>}
        {type === "PERSONAL" && access.existingPersonalId && <Alert severity="info">You already have a personal workspace. Open it above.</Alert>}
        {type === "PERSONAL" && access.enabled && !access.existingPersonalId && !access.canCreate && <Alert severity="info">
          Approval is needed from your business administrator.
          {access.approvals.map((a: any) => <Typography key={a.staffId} variant="body2">{a.businessName}: {a.status.toLowerCase()}</Typography>)}
          <Button disabled={requesting.loading} onClick={() => run(() => request())}>Request personal workspace</Button>
        </Alert>}
        <TextField required label={type === "PERSONAL" ? "Your public name" : "Business name"} value={name} onChange={e => setName(e.target.value)} inputProps={{ maxLength: 200 }} />
        <TextField required label="Booking link" value={slug} onChange={e => setSlug(e.target.value.toLowerCase())} error={Boolean(getBusinessSlugAdvisory(slug))} helperText={getBusinessSlugAdvisory(slug) || `timely365.com/${slug || "your-name"}`} inputProps={{ pattern: "[a-z0-9]+(-[a-z0-9]+)*", maxLength: 150 }} />
        <TextField required select label="Time zone" value={timeZones.length ? zone : ""} onChange={e => setZone(e.target.value)}>
          {timeZones.map((item: any) => <MenuItem key={item.id} value={item.timezone}>{item.timezone}</MenuItem>)}
        </TextField>
        <Typography variant="body2">Your shared page will say “Book with {name || (type === "PERSONAL" ? "your name" : "your business name")}”. Each workspace has its own link. Existing workspace data is not copied.</Typography>
        <Button type="submit" variant="contained" disabled={busy || creating.loading || switching.loading || personalBlocked || Boolean(getBusinessSlugAdvisory(slug))}>{busy ? "Setting up workspace…" : createdId.current ? "Open created workspace" : `Create ${type === "PERSONAL" ? "personal" : "business"} workspace`}</Button>
        {businesses.length > 0 && <Button disabled={busy} onClick={() => { setShowCreate(false); setMessage(""); }}>Cancel</Button>}
      </Stack></CardContent></Card>}
    </Stack>
  </Box>;
}
